import React from "react"
import { graphql } from "gatsby"
import { makeStyles } from "@material-ui/core/styles"
import Layout from "../components/layout"
import Container from "@material-ui/core/Container"
import Grid from "@material-ui/core/Grid"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import MainToolbar from "../components/main-toolbar"
import SEO from "../components/seo"
import { injectIntl, Link } from "gatsby-plugin-intl"
import LineTitle from "../components/LineTitle"
import BackgroundImage from "gatsby-background-image"
import Img from "gatsby-image"
import PatientMap, { PATIENT_COUNTRIES } from "../components/PatientMap"
import VideoBlock from "../components/VideoBlock"

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: "#000",
    },
  },
  mainContainer: {
    padding: "0 !important",
    paddingBottom: "120px",
    backgroundColor: "#202020",
    overflowX: "hidden",
    [theme.breakpoints.down("md")]: {
      paddingBottom: "60px",
    },
  },
  bannerGrid: {
    flexDirection: "row",
    position: "relative",
    zIndex: 2,
    ["@media (max-width:600px)"]: {
      paddingBottom: "0px",
      height: "42vh",
    },
  },
  heroOverlay: {
    display: "none",
    ["@media (max-width:600px)"]: {
      display: "block",
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.4)",
      pointerEvents: "none",
      zIndex: 1,
    },
  },
  bannerLeft: {
    display: "flex",
    justifyContent: "flex-start",
    paddingLeft: 20,
    [theme.breakpoints.down("md")]: {
      paddingLeft: 0,
    },
  },
  heroPlaceholder: {
    margin: 0,
    padding: 0,
    backgroundColor: "#333",
    position: "relative",
    paddingBottom: theme.spacing(5),
    [theme.breakpoints.up("sm")]: {
      minHeight: "600px",
    },
    [theme.breakpoints.down("md")]: {
      height: "280px",
    },
    ["@media (max-width:600px)"]: {
      height: "380px",
      backgroundPosition: "82% center !important",
      "&:before, &:after": {
        backgroundPosition: "82% center !important",
      },
    },
  },
  tooLongtitle: {
    color: "white",
    maxWidth: 560,
    fontWeight: 400,
    ["@media (min-width:1440px)"]: {
      fontSize: "5rem",
    },
    ["@media (max-width:600px)"]: {
      fontSize: "2rem",
      wordSpacing: "100vw",
      lineHeight: 1.1,
    },
  },
  tooLongSubheader: {
    color: "silver",
    textTransform: "uppercase",
    align: "left",
    marginTop: "20px",
    maxWidth: "650px",
    ["@media (max-width:600px)"]: {
      display: "none",
    },
  },
  section: {
    width: "100%",
    padding: "120px 5% 80px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      padding: "70px 20px",
    },
  },
  sectionDark: {
    backgroundColor: "#202020",
  },
  sectionGradient: {
    background: "#282828",
  },
  sectionGrid: {
    alignItems: "center",
  },
  textColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    padding: 0,
    margin: 0,
    maxWidth: "600px",
  },
  imageColumn: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      marginTop: "12px",
    },
  },
  sectionLabel: {
    color: "silver",
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    fontSize: "0.9rem",
    fontWeight: 500,
    marginBottom: "16px",
  },
  sectionHeader: {
    color: "white",
    fontWeight: 300,
    marginBottom: "20px",
    textAlign: "left",
  },
  missionTagline: {
    color: "white",
    fontWeight: 300,
    marginBottom: "32px",
    textAlign: "left",
    whiteSpace: "pre-line",
    lineHeight: 1.15,
  },
  sectionTagline: {
    color: "white",
    fontWeight: 400,
    fontSize: "1.6rem",
    marginBottom: "24px",
    letterSpacing: "0.04em",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.3rem",
    },
  },
  bodyText: {
    fontSize: "1.1rem",
    color: "white",
    whiteSpace: "pre-line",
    margin: 0,
    marginBottom: "20px",
    maxWidth: "720px",
  },
  introText: {
    fontSize: "1.2rem",
    color: "white",
    fontWeight: 400,
    marginBottom: "24px",
    maxWidth: "720px",
  },
  imagePlaceholder: {
    width: "100%",
    maxWidth: "500px",
    height: "420px",
    backgroundColor: "#333",
    borderRadius: "4px",
    [theme.breakpoints.down("sm")]: {
      height: "280px",
    },
  },
  sectionImage: {
    width: "100%",
    maxWidth: "500px",
    borderRadius: "4px",
    [theme.breakpoints.down("sm")]: {
      width: "calc(100% + 88px)",
      maxWidth: "calc(100% + 88px)",
      marginLeft: "-44px",
      marginRight: "-44px",
      borderRadius: 0,
    },
  },
  mapSection: {
    backgroundColor: "#202020",
    width: "100%",
    padding: "120px 5% 80px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      padding: "70px 20px",
    },
  },
  mapContainer: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  mapHeader: {
    color: "white",
    fontWeight: 300,
    marginBottom: "20px",
    textAlign: "center",
  },
  mapText: {
    fontSize: "1.1rem",
    color: "white",
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto 40px",
  },
  mapPlaceholder: {
    width: "100%",
    height: "520px",
    backgroundColor: "#333",
    borderRadius: "4px",
    [theme.breakpoints.down("md")]: {
      height: "360px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "240px",
    },
  },
  principlesSection: {
    background: "#282828",
    width: "100%",
    padding: "140px 5% 100px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      padding: "90px 20px",
    },
  },
  principlesContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "center",
  },
  principlesHeader: {
    color: "white",
    fontWeight: 300,
    marginBottom: "24px",
  },
  principlesText: {
    fontSize: "1.25rem",
    color: "white",
    fontStyle: "italic",
    maxWidth: "780px",
    margin: "0 auto",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  teamSection: {
    backgroundColor: "#202020",
    width: "100%",
    padding: "120px 5% 80px",
    margin: 0,
    [theme.breakpoints.down("sm")]: {
      padding: "70px 20px",
    },
  },
  teamContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "center",
  },
  teamLabel: {
    color: "silver",
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    fontSize: "0.9rem",
    fontWeight: 500,
    marginBottom: "16px",
  },
  teamTitle: {
    color: "white",
    fontWeight: 300,
    marginBottom: "20px",
  },
  teamText: {
    fontSize: "1.1rem",
    color: "white",
    maxWidth: "720px",
    margin: "0 auto 64px",
    lineHeight: 1.6,
    [theme.breakpoints.down("sm")]: {
      marginBottom: "48px",
    },
  },
  teamCtaLink: {
    display: "inline-block",
    textDecoration: "none",
  },
  teamCta: {
    color: "white!important",
    fontSize: "14px!important",
    fontWeight: "bold",
    backgroundColor: "transparent",
    border: "2px solid white",
    borderRadius: "40px!important",
    padding: "0.8em 2.5em!important",
    textDecoration: "none!important",
    "&:hover": {
      textDecoration: "none!important",
      opacity: "0.7!important",
      backgroundColor: "transparent!important",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "0.8em 1.5em!important",
    },
  },
  resultsLink: {
    color: "#C8AA78",
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 500,
    "&:hover": {
      opacity: 0.75,
    },
  },
  mapCountriesIntro: {
    fontSize: "1.1rem",
    color: "white",
    textAlign: "center",
    maxWidth: "800px",
    margin: "56px auto 36px",
  },
  countryList: {
    listStyle: "none",
    padding: 0,
    margin: "0 auto",
    maxWidth: "780px",
    columnGap: "40px",
    columnCount: 1,
    [theme.breakpoints.up("sm")]: {
      columnCount: 2,
    },
    [theme.breakpoints.up("md")]: {
      columnCount: 3,
    },
  },
  countryItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "7px 0",
    color: "white",
    fontSize: "1rem",
    breakInside: "avoid",
    WebkitColumnBreakInside: "avoid",
  },
  flag: {
    width: "28px",
    height: "auto",
    flexShrink: 0,
    display: "block",
    borderRadius: "2px",
    border: "1px solid rgba(255, 255, 255, 0.12)",
  },
}))

const AboutFaceInstitute = ({ intl, data, location }) => {
  const classes = useStyles()
  const t = (id) => intl.formatMessage({ id })
  const bannerSources = [
    data.bannerMobileImage.childImageSharp.fluid,
    {
      ...data.bannerImage.childImageSharp.fluid,
      media: `(min-width: 601px)`,
    },
  ]
  const doctorsImage = data.doctorsImage.childImageSharp.fluid
  const resultsImage = data.resultsImage.childImageSharp.fluid
  const videoPoster = data.videoPoster.childImageSharp.fluid

  return (
    <Layout>
      <SEO
        lang={t("global.lang")}
        title={t("aboutFaceInstitute.seoTitle")}
        description={t("aboutFaceInstitute.seoDescription")}
        path={location.pathname}
      />
      <MainToolbar black />
      <Container maxWidth={"xl"} className={classes.mainContainer}>
        {/* Hero Section */}
        <section>
          <BackgroundImage
            fluid={bannerSources}
            className={classes.heroPlaceholder}
            backgroundColor={`#333`}
          >
            <div className={classes.heroOverlay} aria-hidden="true" />
            <Grid container spacing={0} className={classes.bannerGrid}>
              <Grid item xs={12} sm={6} className={classes.bannerLeft}>
                <Container>
                  <LineTitle
                    header={t("aboutFaceInstitute.title")}
                    subheader={t("aboutFaceInstitute.subtitle")}
                    titleStyle={classes.tooLongtitle}
                    lightDecoLine
                    subHeaderStyle={classes.tooLongSubheader}
                  />
                </Container>
              </Grid>
              <Grid item xs={12} sm={6}></Grid>
            </Grid>
          </BackgroundImage>
        </section>

        {/* Mission Statement */}
        <section className={`${classes.section} ${classes.sectionGradient}`}>
          <Grid container spacing={6} className={classes.sectionGrid}>
            <Grid item xs={12} md={6}>
              <div className={classes.textColumn}>
                <Typography className={classes.sectionLabel}>
                  {t("aboutFaceInstitute.missionLabel")}
                </Typography>
                <Typography
                  component="h2"
                  variant="h3"
                  className={classes.missionTagline}
                >
                  {t("aboutFaceInstitute.missionTagline")}
                </Typography>
                <p className={classes.bodyText}>
                  {t("aboutFaceInstitute.missionText")}
                </p>
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={classes.imageColumn}>
              <Img
                fluid={doctorsImage}
                alt={
                  intl.locale === "nl"
                    ? "Drs. Susan Schmidt en Dr. Callum Faris"
                    : "Dr Susan Schmidt and Dr Callum Faris"
                }
                className={classes.sectionImage}
                imgStyle={{ borderRadius: "4px" }}
              />
            </Grid>
          </Grid>
        </section>

        {/* What We Do */}
        <section className={`${classes.section} ${classes.sectionDark}`}>
          <Grid
            container
            spacing={6}
            className={classes.sectionGrid}
            direction="row-reverse"
          >
            <Grid item xs={12} md={6}>
              <div className={classes.textColumn}>
                <Typography
                  component="h2"
                  variant="h3"
                  className={classes.sectionHeader}
                >
                  {t("aboutFaceInstitute.whatWeDoTitle")}
                </Typography>
                <p className={classes.introText}>
                  {t("aboutFaceInstitute.whatWeDoIntro")}
                </p>
                <p className={classes.bodyText}>
                  {t("aboutFaceInstitute.whatWeDoText")}
                </p>
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={classes.imageColumn}>
              <VideoBlock
                posterFluid={videoPoster}
                posterAlt={t("aboutFaceInstitute.whatWeDoVideoPosterAlt")}
                webmSrc="/videos/facelift-transformations.webm"
                mp4Src="/videos/facelift-transformations.mp4"
                title={t("aboutFaceInstitute.whatWeDoVideoTitle")}
                description={t("aboutFaceInstitute.whatWeDoVideoDescription")}
                playLabel={t("aboutFaceInstitute.whatWeDoVideoPlayLabel")}
                uploadDate="2026-05-15"
                durationISO="PT1M48S"
              />
            </Grid>
          </Grid>
        </section>

        {/* Results */}
        <section className={`${classes.section} ${classes.sectionGradient}`}>
          <Grid container spacing={6} className={classes.sectionGrid}>
            <Grid item xs={12} md={6}>
              <div className={classes.textColumn}>
                <Typography
                  component="h2"
                  variant="h3"
                  className={classes.sectionHeader}
                >
                  {t("aboutFaceInstitute.resultsTitle")}
                </Typography>
                <p className={classes.introText}>
                  {t("aboutFaceInstitute.resultsIntro")}
                </p>
                <p className={classes.bodyText}>
                  {t("aboutFaceInstitute.resultsText")}
                </p>
                <p className={classes.bodyText}>
                  {t("aboutFaceInstitute.resultsMorePre")}
                  <Link
                    to="/deep-plane-facelift"
                    className={classes.resultsLink}
                  >
                    {t("aboutFaceInstitute.resultsMoreLink")}
                  </Link>
                  {t("aboutFaceInstitute.resultsMoreSuf")}
                </p>
              </div>
            </Grid>
            <Grid item xs={12} md={6} className={classes.imageColumn}>
              <Img
                fluid={resultsImage}
                alt="Before and after results"
                className={classes.sectionImage}
                imgStyle={{ borderRadius: "4px" }}
              />
            </Grid>
          </Grid>
        </section>

        {/* Core Principles */}
        <section className={classes.principlesSection}>
          <div className={classes.principlesContainer}>
            <Typography
              component="h2"
              variant="h3"
              className={classes.principlesHeader}
            >
              {t("aboutFaceInstitute.principlesTitle")}
            </Typography>
            <p className={classes.principlesText}>
              {t("aboutFaceInstitute.principlesText")}
            </p>
          </div>
        </section>

        {/* Meet the Team CTA */}
        <section className={classes.teamSection}>
          <div className={classes.teamContainer}>
            <Typography className={classes.teamLabel}>
              {t("aboutFaceInstitute.teamLabel")}
            </Typography>
            <Typography
              component="h2"
              variant="h3"
              className={classes.teamTitle}
            >
              {t("aboutFaceInstitute.teamTitle")}
            </Typography>
            <p className={classes.teamText}>
              {t("aboutFaceInstitute.teamText")}
            </p>
            <Link to="/about" className={classes.teamCtaLink}>
              <Button className={classes.teamCta}>
                {t("aboutFaceInstitute.teamCta")}
              </Button>
            </Link>
          </div>
        </section>

        {/* World Map */}
        <section className={classes.mapSection}>
          <div className={classes.mapContainer}>
            <Typography
              component="h2"
              variant="h3"
              className={classes.mapHeader}
            >
              {t("aboutFaceInstitute.mapTitle")}
            </Typography>
            <p className={classes.mapText}>
              {t("aboutFaceInstitute.mapText")}
            </p>
            <PatientMap />
            <p className={classes.mapCountriesIntro}>
              {t("aboutFaceInstitute.mapCountriesIntro")}
            </p>
            <ul className={classes.countryList}>
              {PATIENT_COUNTRIES.map((country) => (
                <li key={country.id} className={classes.countryItem}>
                  <img
                    src={`/flags/${country.code}.svg`}
                    alt=""
                    aria-hidden="true"
                    width="28"
                    height="19"
                    loading="lazy"
                    className={classes.flag}
                  />
                  <span>
                    {intl.locale === "nl" ? country.nl : country.en}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </Container>
    </Layout>
  )
}

export default injectIntl(AboutFaceInstitute)

export const query = graphql`
  query {
    bannerImage: file(relativePath: { eq: "heros/about-face-institute-banner-v2.webp" }) {
      childImageSharp {
        fluid(maxWidth: 2400, quality: 100) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    bannerMobileImage: file(relativePath: { eq: "heros/about-face-institute-banner-mobile.webp" }) {
      childImageSharp {
        fluid(maxWidth: 900, quality: 90) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    videoPoster: file(relativePath: { eq: "facelift-transformations-poster.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 800, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    doctorsImage: file(relativePath: { eq: "susan-schmidt-callum-faris.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 90) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    resultsImage: file(relativePath: { eq: "before-after-about-face-institute.webp" }) {
      childImageSharp {
        fluid(maxWidth: 1000, quality: 90) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
